'use client'

import { useActionState, useState, useTransition } from 'react'

import { changePassword } from '@/app/actions/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import type { ChangePasswordResult } from '@/app/actions/auth'

function getPasswordRequirements(password: string): Array<{ message: string; met: boolean }> {
  return [
    { message: 'At least 10 characters', met: password.length >= 10 },
    { message: 'At least one lowercase letter', met: /[a-z]/.test(password) },
    { message: 'At least one uppercase letter', met: /[A-Z]/.test(password) },
    {
      message: 'At least one special character',
      met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    },
  ]
}

export function PasswordForm() {
  const [state, formAction] = useActionState<ChangePasswordResult | null, FormData>(
    changePassword,
    null,
  )
  const [isPending, startTransition] = useTransition()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const requirements = getPasswordRequirements(newPassword)
  const passwordMeetsRequirements = requirements.every((r) => r.met)
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword
  const canSubmit = passwordMeetsRequirements && passwordsMatch

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!canSubmit) return
    startTransition(() => {
      formAction(new FormData(e.target as HTMLFormElement))
    })
  }

  return (
    <div className="border-t border-border py-6">
      <div className="m-auto w-fit max-w-xl">
        <h2 className="font-family-body text-2xl font-semibold text-foreground">Change password</h2>
        <p className="mt-1 font-body text-sm text-muted-foreground">
          Use at least 10 characters with uppercase, lowercase, and a special character.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Current password</Label>
            <Input
              id="currentPassword"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              required
              disabled={isPending}
              aria-invalid={!!state?.error}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="newPassword">New password</Label>
            <Input
              id="newPassword"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={isPending}
            />
            <ul className="list-inside list-disc text-xs text-muted-foreground">
              {getPasswordRequirements(newPassword).map((r) => (
                <li key={r.message} className={r.met ? 'text-green-600' : ''}>
                  {r.message}
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm new password</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isPending}
              aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-sm text-destructive">Passwords do not match.</p>
            )}
          </div>
          {state?.error && (
            <p className="text-sm text-destructive" role="alert">
              {state.error}
            </p>
          )}
          {state?.success && (
            <p className="text-sm text-green-600" role="status">
              Password updated. Use your new password next time you sign in.
            </p>
          )}
          <Button type="submit" variant="accent" disabled={!canSubmit || isPending}>
            {isPending ? 'Updating…' : 'Update password'}
          </Button>
        </form>
      </div>
    </div>
  )
}
